import { api } from '@prooffleet/client';

/**
 * Подписка на web push в водительском приложении.
 *
 * Подписка привязана к устройству, а не к пользователю: один человек может
 * открывать портал с рабочего компьютера и с ноутбука, и отписка на одном
 * не должна глушить другой.
 *
 * В payload уходит только `{type, id, severity}` — push проходит через серверы
 * Google и Apple, и содержимое дефектов туда не попадает никогда.
 */

/** Тип возврата — ArrayBuffer, а не Uint8Array: PushManager принимает BufferSource. */
function urlBase64ToBuffer(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes.buffer;
}

/**
 * На iOS push работает ТОЛЬКО у приложения, установленного на домашний экран
 * (16.4+). Открытая вкладка Safari уведомлений не получит никогда, поэтому
 * прежде чем просить разрешение, надо понять, установлено ли приложение.
 */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

export async function subscribeToPush(): Promise<void> {
  if (!pushSupported()) throw new Error('unsupported');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('denied');

  const { publicKey } = await api<{ publicKey: string | null }>('/push/public-key');
  // Без VAPID-ключей канал просто выключен — это не ошибка конфигурации.
  if (publicKey === null || publicKey === '') throw new Error('not_configured');

  const registration =
    (await navigator.serviceWorker.getRegistration()) ??
    (await navigator.serviceWorker.register('/sw.js'));

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToBuffer(publicKey),
  });

  const json = subscription.toJSON();
  await api('/push/subscribe', {
    method: 'POST',
    body: {
      endpoint: subscription.endpoint,
      keys: { p256dh: json.keys?.['p256dh'] ?? '', auth: json.keys?.['auth'] ?? '' },
    },
  });
}

export async function unsubscribeFromPush(): Promise<void> {
  const subscription = await currentSubscription();
  if (subscription === null) return;
  await api('/push/unsubscribe', { method: 'POST', body: { endpoint: subscription.endpoint } });
  await subscription.unsubscribe();
}
